/**
 * Clinical metadata for all 30 statistical tests.
 * Consumed by mockStatisticalData.js and StatisticalTestCard.
 */
export const TEST_CATALOG = [
  {
    id: "paired_t_test",
    name: "Paired t-test",
    category: "group",
    resultId: "paired_t_test",
    type: "Parametrik",
    nRequirement: "N ≥ 30 çift",
    dataType: "Sürekli",
    assumption: "Normallik (farklar)",
    descriptionTR:
      "Aynı hastaların iki farklı takip zamanındaki (Pre-op ve Post-op 6. Ay) skor ortalamalarını karşılaştırır.",
    descriptionEN:
      "Compares mean scores of the same patient cohort across two distinct follow-up timelines.",
    scenarioTR:
      "Örnek Vaka: Ameliyat öncesi ağrı skoru ile ameliyat sonrası 6. ay ağrı skorunun karşılaştırılması.",
    scenarioEN:
      "Example Case: Comparing baseline pre-operative pain scores against 6-month post-operative outcomes.",
    slots: [
      { key: "time_point_1", label: "Zaman Noktası 1 (Pre-op / t0)", type: "continuous", required: true },
      { key: "time_point_2", label: "Zaman Noktası 2 (Post-op / t1)", type: "continuous", required: true },
    ],
  },
  {
    id: "independent_t",
    name: "Independent t-test",
    category: "group",
    resultId: "independent_t",
    type: "Parametrik",
    nRequirement: "N ≥ 20 / grup",
    dataType: "Sürekli",
    assumption: "Normallik + varyans eşitliği",
    descriptionTR:
      "Birbirinden bağımsız iki farklı hasta grubunun (Teknik A ve Teknik B) ortalama skorlarını kıyaslar.",
    descriptionEN:
      "Compares mean scores of two entirely separate and independent patient groups.",
    scenarioTR:
      "Örnek Vaka: İki farklı cerrahi teknik uygulanan hastaların 12. aydaki diz bükme açılarının karşılaştırılması.",
    scenarioEN:
      "Example Case: Comparing 12-month range-of-motion degrees between two different surgical technique cohorts.",
    slots: [
      { key: "variable", label: "Sonuç Değişkeni (Sürekli)", type: "continuous", required: true },
      {
        key: "group_by",
        label: "Grup Değişkeni (Kategorik — önerilen: 2 seviye)",
        type: "categorical",
        cardinality: 2,
        required: true,
      },
    ],
  },
  {
    id: "mann_whitney",
    name: "Mann-Whitney U Test",
    category: "group",
    resultId: "independent_t",
    type: "Non-Parametrik",
    nRequirement: "N ≥ 15 / grup",
    dataType: "Sürekli / sıralı",
    assumption: "Bağımsız gruplar (sıra)",
    descriptionTR:
      "İki bağımsız grubun dağılımlarını ve sıra konumlarını karşılaştırır. Dağılım şekilleri benzer olduğunda sonuç konum/medyan farkı olarak yorumlanabilir.",
    descriptionEN:
      "Compares distributions and rank positions between two independent groups. When distribution shapes are similar, the result can be interpreted as a location/median difference.",
    scenarioTR:
      "Örnek Vaka: Çarpık VAS dağılımına sahip iki tedavi kolunun fonksiyonel skorlarının sıra testi ile analizi.",
    scenarioEN:
      "Example Case: Rank-based comparison of functional scores in two treatment arms with skewed VAS distributions.",
    slots: [
      { key: "variable", label: "Sonuç Değişkeni (Sürekli)", type: "continuous", required: true },
      {
        key: "group_by",
        label: "Grup Değişkeni (Kategorik — önerilen: 2 seviye)",
        type: "categorical",
        cardinality: 2,
        required: true,
      },
    ],
  },
  {
    id: "wilcoxon",
    name: "Wilcoxon Signed-Rank Test",
    category: "group",
    resultId: "paired_t_test",
    type: "Non-Parametrik",
    nRequirement: "N ≥ 20 çift",
    dataType: "Sürekli / sıralı",
    assumption: "Simetrik farklar",
    descriptionTR:
      "Aynı hastada iki ölçüm arasındaki farkın medyanını parametrik olmayan eşleştirilmiş test ile değerlendirir.",
    descriptionEN:
      "Evaluates paired differences using a non-parametric signed-rank procedure.",
    scenarioTR:
      "Örnek Vaka: Pre-op ve 6. ay ordinal fonksiyon skorları arasında Wilcoxon signed-rank analizi.",
    scenarioEN:
      "Example Case: Wilcoxon signed-rank analysis between pre-op and 6-month ordinal function scores.",
    // Two distinct continuous time points (paired design) — not a categorical cardinality constraint.
    slots: [
      { key: "time_point_1", label: "Zaman Noktası 1 (Pre-op / t0)", type: "continuous", required: true },
      { key: "time_point_2", label: "Zaman Noktası 2 (Post-op / t1)", type: "continuous", required: true },
    ],
  },
  {
    id: "anova",
    name: "One-Way ANOVA",
    category: "group",
    resultId: "anova",
    type: "Parametrik",
    nRequirement: "N ≥ 10 / grup (≥3)",
    dataType: "Sürekli",
    assumption: "Normallik + varyans eşitliği",
    descriptionTR:
      "Üç veya daha fazla bağımsız hasta grubunun ortalama skorlarını aynı anda analiz eder.",
    descriptionEN:
      "Analyzes mean scores of three or more independent patient groups simultaneously.",
    scenarioTR:
      "Örnek Vaka: Dört farklı yaş grubundaki hastaların ameliyat sonrası memnuniyet skorlarının incelenmesi.",
    scenarioEN:
      "Example Case: Evaluating post-operative satisfaction scores across four distinct patient age groups.",
    slots: [
      { key: "variable", label: "Sonuç Değişkeni (Sürekli)", type: "continuous", required: true },
      {
        key: "group_by",
        label: "Grup Değişkeni (Kategorik — önerilen: ≥3 seviye)",
        type: "categorical",
        minLevels: 3,
        required: true,
      },
    ],
  },
  {
    id: "kruskal",
    name: "Kruskal-Wallis Test",
    category: "group",
    resultId: "anova",
    type: "Non-Parametrik",
    nRequirement: "N ≥ 8 / grup (≥3)",
    dataType: "Sürekli / sıralı",
    assumption: "Bağımsız gruplar (sıra)",
    descriptionTR:
      "Üç veya daha fazla bağımsız grubun sürekli veya sıralı skorlarını ANOVA’nın parametrik olmayan karşılığı ile karşılaştırır.",
    descriptionEN:
      "Non-parametric extension comparing three or more independent groups via rank sums.",
    scenarioTR:
      "Örnek Vaka: Non-normal KOOS dağılımlarında konservatif, artroskopi ve açık cerrahi kollarının kıyaslanması.",
    scenarioEN:
      "Example Case: Comparing conservative, arthroscopic, and open surgery arms when KOOS is non-normal.",
    slots: [
      { key: "variable", label: "Sonuç Değişkeni (Sürekli)", type: "continuous", required: true },
      {
        key: "group_by",
        label: "Grup Değişkeni (Kategorik — önerilen: ≥3 seviye)",
        type: "categorical",
        minLevels: 3,
        required: true,
      },
    ],
  },
  {
    id: "repeated_anova",
    name: "Repeated-Measures ANOVA",
    category: "group",
    resultId: "anova",
    type: "Parametrik",
    nRequirement: "N ≥ 25 (≥3 zaman)",
    dataType: "Sürekli",
    assumption: "Sferisite",
    descriptionTR:
      "Aynı hastada üç veya daha fazla zaman noktasındaki ölçümler arasındaki ortalama değişimi modeler.",
    descriptionEN:
      "Models within-subject mean changes across three or more longitudinal time points.",
    scenarioTR:
      "Örnek Vaka: Pre-op, 3. ay ve 6. ay KOOS skorlarının longitudinal tekrarlı ölçümler analizi.",
    scenarioEN:
      "Example Case: Longitudinal repeated-measures analysis of KOOS at pre-op, 3 months, and 6 months.",
    // ≥3 continuous time-point slots (within-subject), not a categorical group_by.
    slots: [
      { key: "time_point_1", label: "Zaman Noktası 1 (Pre-op / t0)", type: "continuous", required: true },
      { key: "time_point_2", label: "Zaman Noktası 2 (Ara / t1)", type: "continuous", required: true },
      { key: "time_point_3", label: "Zaman Noktası 3 (Takip / t2)", type: "continuous", required: true },
      { key: "time_point_4", label: "Zaman Noktası 4 (Opsiyonel / t3)", type: "continuous", required: false },
    ],
  },
  {
    id: "friedman",
    name: "Friedman Test",
    category: "group",
    resultId: "anova",
    type: "Non-Parametrik",
    nRequirement: "N ≥ 20 (≥3 zaman)",
    dataType: "Sürekli / sıralı",
    assumption: "İlişkili ölçümler",
    descriptionTR:
      "Tekrarlı ölçümlerde üç veya daha fazla zaman noktası arasındaki farkı parametrik olmayan yöntemle test eder.",
    descriptionEN:
      "Non-parametric test for differences across three or more related measurements.",
    scenarioTR:
      "Örnek Vaka: Üç kontrol ziyaretindeki ordinal ağrı skorları arasında Friedman testi uygulanması.",
    scenarioEN:
      "Example Case: Friedman test across ordinal pain scores at three serial follow-up visits.",
    // ≥3 continuous time-point slots (within-subject), not a categorical group_by.
    slots: [
      { key: "time_point_1", label: "Zaman Noktası 1 (Pre-op / t0)", type: "continuous", required: true },
      { key: "time_point_2", label: "Zaman Noktası 2 (Ara / t1)", type: "continuous", required: true },
      { key: "time_point_3", label: "Zaman Noktası 3 (Takip / t2)", type: "continuous", required: true },
      { key: "time_point_4", label: "Zaman Noktası 4 (Opsiyonel / t3)", type: "continuous", required: false },
    ],
  },
  {
    id: "welch_t",
    name: "Welch t-test",
    category: "group",
    resultId: "independent_t",
    type: "Parametrik",
    nRequirement: "N ≥ 20 / grup",
    dataType: "Sürekli",
    assumption: "Normallik (eşit varyans yok)",
    descriptionTR:
      "Varyans homojenliği bozulduğunda iki bağımsız grubun ortalamalarını düzeltilmiş serbestlik derecesi ile karşılaştırır.",
    descriptionEN:
      "Compares two independent group means when equal-variance assumption does not hold.",
    scenarioTR:
      "Örnek Vaka: Heteroskedastik 12. ay ROM skorlarında Teknik A ve Teknik B karşılaştırması.",
    scenarioEN:
      "Example Case: Welch comparison of 12-month ROM between Technique A and B with unequal variances.",
    slots: [
      { key: "variable", label: "Sonuç Değişkeni (Sürekli)", type: "continuous", required: true },
      {
        key: "group_by",
        label: "Grup Değişkeni (Kategorik — önerilen: 2 seviye)",
        type: "categorical",
        cardinality: 2,
        required: true,
      },
    ],
  },
  {
    id: "descriptive_stats",
    name: "Descriptive Statistics and Variance",
    nameTR: "Betimsel İstatistik ve Varyans",
    nameEN: "Descriptive Statistics and Variance",
    nameFR: "Statistiques descriptives et variance",
    category: "group",
    resultId: "independent_t",
    type: "Betimsel",
    nRequirement: "Her N",
    dataType: "Sürekli",
    assumption: "Betimsel (varsayım yok)",
    descriptionTR:
      "Ortalama, standart sapma, medyan ve Levene testi ile veri setinin dağılım özetini üretir.",
    descriptionEN:
      "Summarizes central tendency, dispersion, and variance homogeneity before inferential testing.",
    scenarioTR:
      "Örnek Vaka: KOOS skorları için grup bazlı ortalama, standart sapma ve Levene sonuç tablosunun oluşturulması.",
    scenarioEN:
      "Example Case: Building a table of mean, SD, and Levene results for KOOS scores by treatment arm.",
    slots: [
      { key: "variable", label: "Sonuç Değişkeni (Sürekli)", type: "continuous", required: true },
      {
        key: "group_by",
        label: "Grup Değişkeni (Opsiyonel)",
        type: "categorical",
        required: false,
      },
    ],
  },
  {
    id: "pearson",
    name: "Pearson Correlation",
    category: "relation",
    resultId: "pearson",
    type: "Parametrik",
    nRequirement: "N ≥ 30",
    dataType: "Sürekli",
    assumption: "Doğrusallık + normallik",
    descriptionTR:
      "İki sürekli klinik değişken arasındaki doğrusal ilişkinin yönünü ve gücünü korelasyon katsayısı ile ölçer.",
    descriptionEN:
      "Quantifies strength and direction of linear association between two continuous variables.",
    scenarioTR:
      "Örnek Vaka: Yaş ile 6. ay KOOS skoru arasındaki Pearson korelasyonunun raporlanması.",
    scenarioEN:
      "Example Case: Reporting Pearson correlation between patient age and 6-month KOOS score.",
  },
  {
    id: "spearman",
    name: "Spearman Correlation",
    category: "relation",
    resultId: "pearson",
    type: "Non-Parametrik",
    nRequirement: "N ≥ 20",
    dataType: "Sürekli / sıralı",
    assumption: "Monoton ilişki",
    descriptionTR:
      "Sıralı veya asimetrik dağılımlı değişkenler arasındaki monoton ilişkiyi rank korelasyonu ile değerlendirir.",
    descriptionEN:
      "Measures monotonic association between ordinal or non-normal continuous variables.",
    scenarioTR:
      "Örnek Vaka: Komplikasyon derecesi (ordinal) ile fonksiyonel skor arasındaki Spearman rho analizi.",
    scenarioEN:
      "Example Case: Spearman rho between ordinal complication grade and functional score.",
  },
  {
    id: "reliability_alpha",
    name: "Cronbach Alpha (Likert Reliability)",
    nameTR: "Cronbach Alpha (Likert Güvenilirlik)",
    nameEN: "Cronbach Alpha (Likert Reliability)",
    nameFR: "Alpha de Cronbach (Fiabilité de Likert)",
    category: "relation",
    resultId: "independent_t",
    type: "Betimsel",
    nRequirement: "N ≥ 30 (≥3 madde)",
    dataType: "Likert / sürekli",
    assumption: "Tek boyutluluk",
    descriptionTR:
      "Likert maddelerinden oluşan hasta-reported ölçeğin iç tutarlılığını Cronbach alfa ile değerlendirir.",
    descriptionEN:
      "Evaluates internal consistency of multi-item patient-reported Likert instruments.",
    scenarioTR:
      "Örnek Vaka: Diz PRO anketi maddelerinde Cronbach α > 0,70 hedefinin doğrulanması.",
    scenarioEN:
      "Example Case: Confirming Cronbach α > 0.70 across items of a knee PRO questionnaire.",
    slots: [
      { key: "time_point_1", label: "Madde / Item 1", type: "continuous", required: true },
      { key: "time_point_2", label: "Madde / Item 2", type: "continuous", required: true },
      { key: "time_point_3", label: "Madde / Item 3", type: "continuous", required: true },
      { key: "time_point_4", label: "Madde / Item 4 (Opsiyonel)", type: "continuous", required: false },
    ],
  },
  {
    id: "inter_observer",
    name: "Cohen Kappa (Inter-Rater Agreement)",
    nameTR: "Cohen Kappa (Gözlemciler Arası Uyum)",
    nameEN: "Cohen Kappa (Inter-Rater Agreement)",
    nameFR: "Kappa de Cohen (Accord inter-évaluateurs)",
    category: "relation",
    resultId: "heatmap",
    type: "Betimsel",
    nRequirement: "N ≥ 30",
    dataType: "Kategorik",
    assumption: "Bağımsız kodlama",
    descriptionTR:
      "İki klinisyenin kategorik görüntü veya klinik sınıflandırma kodları arasındaki uyumu ölçer.",
    descriptionEN:
      "Quantifies agreement between two independent categorical ratings beyond chance.",
    scenarioTR:
      "Örnek Vaka: Pre-op dosya incelemesinde iki cerrah arası cilt kalınlığı (ince/orta/kalın) veya teknik tercihi (preservation/structural) uyumu.",
    scenarioEN:
      "Example Case: Pre-op inter-surgeon agreement on skin thickness (thin/medium/thick) or surgical technique choice.",
    slots: [
      { key: "variable", label: "Kodlayıcı / Gözlemci 1", type: "categorical", required: true },
      { key: "group_by", label: "Kodlayıcı / Gözlemci 2", type: "categorical", required: true },
    ],
  },
  {
    id: "linear_reg",
    name: "Linear Regression",
    category: "regression",
    resultId: "regression",
    type: "Tahmini",
    nRequirement: "N ≥ 40",
    dataType: "Sürekli",
    assumption: "Doğrusallık",
    descriptionTR:
      "Tek bir bağımsız klinik değişkenle sürekli fonksiyonel skorun doğrusal olarak tahmin edilmesini sağlar.",
    descriptionEN:
      "Estimates a continuous functional outcome from a single linear predictor.",
    scenarioTR:
      "Örnek Vaka: BMI değerinden 6. ay KOOS skorunun lineer regresyon ile modellenmesi.",
    scenarioEN:
      "Example Case: Modeling 6-month KOOS from BMI using simple linear regression.",
    slots: [
      {
        key: "variable",
        id: "dependent_variable",
        role: "outcome",
        axis: "y",
        cardinality: 1,
        label: "Hedef / Bağımlı Değişken (Y Ekseni - Sürekli)",
        type: "number",
        dataType: "continuous",
        required: true,
      },
      {
        key: "variable_b",
        id: "independent_variables",
        role: "predictor",
        axis: "x",
        cardinality: 1,
        label: "Bağımsız Öngörücü (X Ekseni - Sürekli)",
        type: "number",
        dataType: "continuous",
        required: true,
      },
    ],
  },
  {
    id: "multiple_reg",
    name: "Multiple Linear Regression",
    category: "regression",
    resultId: "regression",
    type: "Tahmini",
    nRequirement: "N ≥ 50",
    dataType: "Sürekli",
    assumption: "Doğrusallık; çoklu doğrusal olmama",
    descriptionTR:
      "Birden fazla klinik prediktörle sürekli sonucun ayarlanmış varyansını ve katsayıları modeler.",
    descriptionEN:
      "Models a continuous outcome adjusting for multiple simultaneous clinical predictors.",
    scenarioTR:
      "Örnek Vaka: Hastaların Yaş ve BMI (Vücut Kitle İndeksi) değerlerini birlikte kullanarak Sistolik Kan Basıncını tahmin etme.",
    scenarioEN:
      "Example Case: Predicting Systolic Blood Pressure using both Age and BMI as independent predictors.",
    slots: [
      {
        key: "variable",
        id: "dependent_variable",
        role: "outcome",
        axis: "y",
        cardinality: 1,
        label: "Hedef / Bağımlı Değişken (Y Ekseni - Sürekli)",
        type: "number",
        dataType: "continuous",
        required: true,
      },
      {
        key: "variable_b",
        id: "independent_variables",
        role: "predictor",
        axis: "x",
        cardinality: 1,
        label: "1. Bağımsız Öngörücü (X Ekseni - Sürekli)",
        type: "number",
        dataType: "continuous",
        required: true,
      },
      {
        key: "x_col_b",
        id: "independent_variables_b",
        role: "predictor",
        axis: "x",
        cardinality: 1,
        label: "2. Bağımsız Öngörücü (Z Ekseni - Sürekli)",
        type: "number",
        dataType: "continuous",
        required: true,
      },
    ],
  },
  {
    id: "poisson",
    name: "Poisson Regression",
    category: "regression",
    resultId: "regression",
    type: "Tahmini",
    nRequirement: "N ≥ 30",
    dataType: "Sayım (count)",
    assumption: "Poisson (ort. ≈ varyans)",
    descriptionTR:
      "Takip süresindeki komplikasyon veya olay sayısını exposure süresi ile birlikte modelleyen sayım regresyonudur.",
    descriptionEN:
      "Regression for count outcomes such as complications per follow-up with exposure adjustment.",
    scenarioTR:
      "Örnek Vaka: 24 aylık takipte revizyon sayısının hasta-yıl exposure ile Poisson regresyonu.",
    scenarioEN:
      "Example Case: Poisson model of revision counts over 24-month follow-up with patient-years exposure.",
    slots: [
      { key: "variable", label: "Sayım / Count Sonucu (Y)", type: "number", required: true },
      { key: "variable_b", label: "Bağımsız Öngörücü (X)", type: "number", required: true },
    ],
  },
  {
    id: "chi_square",
    name: "Chi-Square Test of Independence",
    category: "categorical",
    resultId: "chi_square",
    type: "Parametrik",
    nRequirement: "N ≥ 40",
    dataType: "Kategorik",
    assumption: "Beklenen hücre ≥ 5",
    descriptionTR:
      "İki kategorik klinik değişkenin (tedavi tipi ve iyileşme durumu) birbirinden bağımsız olup olmadığını test eder.",
    descriptionEN:
      "Tests statistical independence between two categorical clinical variables in a contingency table.",
    scenarioTR:
      "Örnek Vaka: Cerrahi ve konservatif kolda iyileşme evet/hayır oranlarının çapraz tablo analizi.",
    scenarioEN:
      "Example Case: Cross-tab analysis of recovery yes/no rates in surgical versus conservative arms.",
  },
  {
    id: "fisher",
    name: "Fisher Exact Test",
    category: "categorical",
    resultId: "chi_square",
    type: "Parametrik",
    nRequirement: "2×2 – 5×5 tablo",
    dataType: "Kategorik",
    assumption: "Küçük beklenen hücre",
    descriptionTR:
      "Küçük örneklemli çapraz tablolarda (2×2'den 5×5'e kadar) beklenen hücre sayısı yetersizken kesin olasılık testi.",
    descriptionEN:
      "Exact probability test for small-sample cross-tables (from 2×2 up to 5×5) when expected cell counts are too low.",
    descriptionFR:
      "Test de probabilité exacte pour les tableaux croisés de petits échantillons (de 2×2 à 5×5) lorsque les effectifs attendus sont trop faibles.",
    scenarioTR:
      "Örnek Vaka: 22 hastalık nadir komplikasyon tablosunda tedavi ve sonuç ilişkisinin kesin testi.",
    scenarioEN:
      "Example Case: Exact test of treatment-outcome association in a 22-patient rare complication table.",
    slots: [
      {
        key: "variable",
        label: "Kategorik Değişken 1 (2–5 kategori)",
        labelTR: "Kategorik Değişken 1 (2–5 kategori)",
        labelEN: "Categorical Variable 1 (2–5 categories)",
        labelFR: "Variable catégorielle 1 (2–5 catégories)",
        type: "categorical",
        minLevels: 2,
        maxLevels: 5,
        required: true,
      },
      {
        key: "group_by",
        label: "Kategorik Değişken 2 (2–5 kategori)",
        labelTR: "Kategorik Değişken 2 (2–5 kategori)",
        labelEN: "Categorical Variable 2 (2–5 categories)",
        labelFR: "Variable catégorielle 2 (2–5 catégories)",
        type: "categorical",
        minLevels: 2,
        maxLevels: 5,
        required: true,
      },
    ],
  },
  {
    id: "mcnemar",
    name: "McNemar Test",
    category: "frequency",
    resultId: "chi_square",
    type: "Parametrik",
    nRequirement: "N ≥ 25 çift",
    dataType: "İkili kategorik",
    assumption: "Eşleştirilmiş ikili",
    descriptionTR:
      "Aynı hastada iki zaman noktasındaki ikili kategorik sonuçların (ör. başarı) değişimini test eder.",
    descriptionEN:
      "Tests paired shifts in binary categorical outcomes across two time points.",
    scenarioTR:
      "Örnek Vaka: Taburculuk ve 6. ay klinik başarı durumu arasında McNemar analizi.",
    scenarioEN:
      "Example Case: McNemar analysis between discharge status and 6-month clinical success.",
    slots: [
      {
        key: "time_point_1",
        label: "Zaman Noktası 1 (İkili Kategorik)",
        type: "binary",
        role: "categorical",
        cardinality: 2,
        required: true,
      },
      {
        key: "time_point_2",
        label: "Zaman Noktası 2 (İkili Kategorik)",
        type: "binary",
        role: "categorical",
        cardinality: 2,
        required: true,
      },
    ],
  },
  {
    id: "cochran_q",
    name: "Cochran Q Test",
    category: "frequency",
    resultId: "independent_t",
    type: "Parametrik",
    nRequirement: "N ≥ 20 (≥3 zaman)",
    dataType: "İkili kategorik",
    assumption: "Eşleştirilmiş çoklu zaman",
    descriptionTR:
      "Aynı hastada üç veya daha fazla zaman noktasındaki ikili kategorik sonuç oranlarını karşılaştırır.",
    descriptionEN:
      "Compares paired binary outcome proportions across three or more related time points.",
    scenarioTR:
      "Örnek Vaka: Pre-op, 3. ay ve 6. ay komplikasyon var/yok oranlarının Cochran Q ile analizi.",
    scenarioEN:
      "Example Case: Cochran Q for complication yes/no rates at pre-op, 3 months, and 6 months.",
    slots: [
      {
        key: "time_point_1",
        label: "Zaman Noktası 1 (İkili / Kategorik)",
        type: "binary",
        required: true,
      },
      {
        key: "time_point_2",
        label: "Zaman Noktası 2 (İkili / Kategorik)",
        type: "binary",
        required: true,
      },
      {
        key: "time_point_3",
        label: "Zaman Noktası 3 (İkili / Kategorik)",
        type: "binary",
        required: true,
      },
      {
        key: "time_point_4",
        label: "Zaman Noktası 4 (Opsiyonel)",
        type: "binary",
        required: false,
      },
    ],
  },
  {
    id: "binomial",
    name: "Binomial Test",
    category: "frequency",
    resultId: "chi_square",
    type: "Parametrik",
    nRequirement: "N ≥ 20",
    dataType: "İkili / kategorik",
    assumption: "Bağımsız deneme",
    descriptionTR:
      "Gözlenen klinik başarı oranının önceden tanımlı hedef orana (ör. %70) uygunluğunu test eder.",
    descriptionEN:
      "Tests whether an observed clinical success proportion differs from a prespecified target rate.",
    scenarioTR:
      "Örnek Vaka: 6. ayda %70 iyileşme hedefinin kohort başarı oranıyla karşılaştırılması.",
    scenarioEN:
      "Example Case: Testing whether 6-month cohort success rate meets a 70% clinical target.",
    slots: [
      {
        key: "variable",
        label: "İkili / Kategorik Sonuç Değişkeni",
        type: "categorical",
        role: "categorical",
        required: true,
      },
    ],
  },
  {
    id: "chi_gof",
    name: "Chi-Square Goodness-of-Fit",
    category: "frequency",
    resultId: "chi_square",
    type: "Parametrik",
    nRequirement: "N ≥ 20",
    dataType: "Kategorik",
    assumption: "Beklenen hücre ≥ 5",
    descriptionTR:
      "Gözlenen kategorik dağılımın (ör. komplikasyon sınıfları) teorik veya referans dağılıma uyumunu değerlendirir.",
    descriptionEN:
      "Assesses goodness-of-fit between observed and expected category frequencies.",
    scenarioTR:
      "Örnek Vaka: Komplikasyon sınıflarının literatür referans oranlarına uyumunun test edilmesi.",
    scenarioEN:
      "Example Case: Testing whether observed complication classes match published reference proportions.",
    slots: [
      {
        key: "variable",
        label: "Kategorik Değişken",
        type: "categorical",
        role: "categorical",
        required: true,
      },
    ],
  },
  {
    id: "logistic_reg",
    name: "Logistic Regression",
    category: "regression",
    resultId: "forest",
    type: "Tahmini",
    nRequirement: "N ≥ 50",
    dataType: "İkili + prediktör",
    assumption: "İkili sonuç + bağımsızlık",
    descriptionTR:
      "İkili klinik sonucun (başarı veya başarısızlık) olasılığını bir veya daha fazla prediktörle logit modelde tahmin eder.",
    descriptionEN:
      "Estimates probability of a binary clinical outcome using one or more predictors in a logit model.",
    scenarioTR:
      "Örnek Vaka: Pre-op KOOS ve yaş ile 12. ay klinik başarı olasılığının odds oranı analizi.",
    scenarioEN:
      "Example Case: Odds-ratio model of 12-month success from baseline KOOS and age.",
    slots: [
      {
        key: "variable",
        label: "Hedef / Bağımlı Değişken (İkili Sonuç)",
        type: "binary",
        required: true,
      },
      {
        key: "variable_b",
        label: "1. Bağımsız Öngörücü (Sürekli / Kategorik)",
        type: "any",
        required: true,
      },
      {
        key: "x_col_b",
        label: "2. Bağımsız Öngörücü (Sürekli / Kategorik)",
        type: "any",
        required: true,
      },
    ],
  },
  {
    id: "multivariable_logistic",
    name: "Multivariable Logistic Regression (aOR)",
    category: "regression",
    resultId: "multivariable_logistic",
    type: "Tahmini",
    nRequirement: "N ≥ 50",
    dataType: "İkili sonuç + Çoklu prediktör",
    assumption: "İkili sonuç + Bağımsızlık",
    descriptionTR:
      "Klinik kovaryatların bağımsız etkilerini Düzeltilmiş Odds Oranları (aOR & %95 GA) ve p değerleri ile modelleyerek raporlar.",
    descriptionEN:
      "Models independent adjusted effects of clinical covariates reporting Adjusted Odds Ratios (aOR & 95% CI) and p-values.",
    scenarioTR:
      "Örnek Vaka: Hastalık tanısında yaş, cinsiyet ve VKİ düzeltmesi sonrası biyobelirteç düzeylerinin aOR tablosu.",
    scenarioEN:
      "Example Case: aOR table of biomarker levels adjusted for age, gender, and BMI in clinical classification.",
    slots: [
      {
        key: "variable",
        label: "Hedef / Bağımlı Değişken (İkili Sonuç)",
        type: "binary",
        required: true,
      },
      {
        key: "variable_b",
        label: "1. Bağımsız Öngörücü (Sürekli / Kategorik)",
        type: "any",
        required: true,
      },
      {
        key: "x_col_b",
        label: "2. Bağımsız Öngörücü (Sürekli / Kategorik)",
        type: "any",
        required: true,
      },
      {
        key: "x_col_c",
        label: "3. Bağımsız Öngörücü (Opsiyonel)",
        type: "any",
        required: false,
      },
      {
        key: "x_col_d",
        label: "4. Bağımsız Öngörücü (Opsiyonel)",
        type: "any",
        required: false,
      },
      {
        key: "x_col_e",
        label: "5. Bağımsız Öngörücü (Opsiyonel)",
        type: "any",
        required: false,
      },
      {
        key: "x_col_f",
        label: "6. Bağımsız Öngörücü (Opsiyonel)",
        type: "any",
        required: false,
      },
    ],
  },
  {
    id: "multinomial",
    name: "Multinomial Logistic Regression",
    category: "regression",
    resultId: "forest",
    type: "Tahmini",
    nRequirement: "N ≥ 60 (≥3 sınıf)",
    dataType: "Çok kategorili + prediktör",
    assumption: "Bağımsızlık",
    descriptionTR:
      "Üç veya daha fazla sırasız kategorik sonucun (kötü, orta, iyi) birden fazla prediktörle modellenmesini sağlar.",
    descriptionEN:
      "Models unordered multi-category clinical outcomes with multiple predictors.",
    scenarioTR:
      "Örnek Vaka: 12. ay iyileşme derecesi (kötü/orta/iyi) için multinomial lojistik regresyon.",
    scenarioEN:
      "Example Case: Multinomial logistic regression of 12-month recovery grade (poor/fair/good).",
    slots: [
      {
        key: "variable",
        label: "Çok Kategorili Sonuç (önerilen: ≥3 seviye)",
        type: "categorical",
        minLevels: 3,
        required: true,
      },
      { key: "variable_b", label: "1. Bağımsız Öngörücü", type: "any", required: true },
      { key: "x_col_b", label: "2. Bağımsız Öngörücü (Opsiyonel)", type: "any", required: false },
    ],
  },
  {
    id: "ordinal_reg",
    name: "Ordinal Logistic Regression",
    nameTR: "Ordinal Lojistik Regresyon",
    nameEN: "Ordinal Logistic Regression",
    category: "regression",
    resultId: "ordinal_reg",
    type: "Tahmini",
    nRequirement: "N ≥ 50 (≥3 kategori)",
    dataType: "Ordinal sonuç + prediktör",
    assumption: "Proportional Odds (Parallel Lines)",
    descriptionTR:
      "Doğal sırası olan kategorik sonuçları (evre 0–3, hafif–orta–ağır, fonksiyonel derece, Likert) tek bir maruz kalma değişkeni ve kovaryatlarla kümülatif logit modelinde analiz eder. OR > 1 daha yüksek outcome kategorisinde bulunma odds'unun daha yüksek olduğunu gösterir.",
    descriptionEN:
      "Analyzes naturally ordered categorical outcomes (stage 0–3, mild–moderate–severe, functional grade, Likert) using a proportional odds cumulative logit model with primary exposure and optional covariates. OR > 1 indicates higher odds of being in a higher outcome category.",
    scenarioTR:
      "Örnek Vaka: Hastalık evresi (Evre 0–1–2–3) ile yaş arasındaki ordinal ilişkinin proporsiyon odds regresyonu ile incelenmesi; cinsiyet ve komorbidite kovaryatı olarak eklenmesi.",
    scenarioEN:
      "Example Case: Ordinal association between disease stage (Stage 0–1–2–3) and age using proportional odds regression, adjusting for sex and comorbidity.",
    slots: [
      {
        key: "variable",
        id: "outcome_col",
        role: "outcome",
        label: "Ordinal Sonuç Değişkeni (≥3 sıralı kategori)",
        labelTR: "Ordinal Sonuç Değişkeni (≥3 sıralı kategori)",
        labelEN: "Ordinal Outcome Variable (≥3 ordered categories)",
        type: "categorical",
        minLevels: 3,
        required: true,
      },
      {
        key: "variable_b",
        id: "exposure_col",
        role: "exposure",
        label: "Ana Bağımsız Değişken / Maruziyet (Sürekli veya Kategorik)",
        labelTR: "Ana Bağımsız Değişken / Maruziyet",
        labelEN: "Primary Exposure / Independent Variable",
        type: "any",
        required: true,
      },
      {
        key: "x_col_b",
        id: "covariate_1",
        role: "covariate",
        label: "Kovaryat 1 (Opsiyonel)",
        labelTR: "Kovaryat 1 (Opsiyonel)",
        labelEN: "Covariate 1 (Optional)",
        type: "any",
        required: false,
      },
      {
        key: "x_col_c",
        id: "covariate_2",
        role: "covariate",
        label: "Kovaryat 2 (Opsiyonel)",
        labelTR: "Kovaryat 2 (Opsiyonel)",
        labelEN: "Covariate 2 (Optional)",
        type: "any",
        required: false,
      },
      {
        key: "x_col_d",
        id: "covariate_3",
        role: "covariate",
        label: "Kovaryat 3 (Opsiyonel)",
        labelTR: "Kovaryat 3 (Opsiyonel)",
        labelEN: "Covariate 3 (Optional)",
        type: "any",
        required: false,
      },
    ],
  },
  {
    id: "kaplan_meier",
    name: "Kaplan-Meier Survival",
    category: "advanced",

    resultId: "kaplan_meier",
    type: "Betimsel",
    nRequirement: "Zaman + olay",
    dataType: "Süre + ikili olay",
    assumption: "Sağ sansürleme",
    descriptionTR:
      "Protez veya implant sağkalım sürelerini ve revizyon ameliyatına kadar geçen klinik ömrü hesaplar.",
    descriptionEN:
      "Measures implant longevity profiles and longitudinal survival timelines before failure.",
    scenarioTR:
      "Örnek Vaka: Belirli bir diz protezi modelinin 10 yıllık revizyonsuz hayatta kalma eğrisinin çizilmesi.",
    scenarioEN:
      "Example Case: Plotting 10-year revision-free structural survival for a specific knee prosthesis model.",
    slots: [
      { key: "time_column", label: "Süre / Zaman Sütunu", type: "continuous", required: true },
      { key: "event_column", label: "Olay Değişkeni (Binary/Kategorik)", type: "binary", required: true },
      { key: "stratifier", label: "Stratifikasyon Grubu (Opsiyonel)", type: "categorical", required: false },
    ],
  },
  {
    id: "log_rank",
    name: "Log-Rank Test",
    category: "advanced",
    resultId: "kaplan_meier",
    type: "Non-Parametrik",
    nRequirement: "≥2 grup",
    dataType: "Süre + ikili olay",
    assumption: "Orantılı hazard",
    descriptionTR:
      "İki veya daha fazla Kaplan-Meier eğrisinin zamanla ayrılıp ayrılmadığını log-rank istatistiği ile test eder.",
    descriptionEN:
      "Tests whether two or more Kaplan-Meier survival curves differ over follow-up time.",
    scenarioTR:
      "Örnek Vaka: Teknik A ve Teknik B implant sağkalım eğrileri arasında log-rank p değeri raporu.",
    scenarioEN:
      "Example Case: Log-rank p-value comparing implant survival curves for Technique A versus B.",
    slots: [
      { key: "time_column", label: "Süre / Zaman Sütunu", type: "continuous", required: true },
      { key: "event_column", label: "Olay Değişkeni (Binary/Kategorik)", type: "binary", required: true },
      {
        key: "group_by",
        label: "Grup Değişkeni (Kategorik — önerilen: ≥2 seviye)",
        type: "categorical",
        minLevels: 2,
        required: true,
      },
    ],
  },
  {
    id: "cox",
    name: "Cox Proportional Hazards",
    category: "advanced",
    resultId: "forest",
    type: "Tahmini",
    nRequirement: "Olay ≥ 10 / kovaryat",
    dataType: "Süre + olay + kovaryat",
    assumption: "Orantılı hazard",
    descriptionTR:
      "Çoklu klinik kovaryatlarla ayarlanmış hazard oranını ve revizyon riskini zamanla modelleyen sağkalım regresyonudur.",
    descriptionEN:
      "Multivariable survival regression estimating adjusted hazard ratios over time.",
    scenarioTR:
      "Örnek Vaka: Yaş ve BMI düzeltildikten sonra cerrahi tekniğin revizyon hazard oranı analizi.",
    scenarioEN:
      "Example Case: Cox hazard ratios for revision risk by technique adjusting for age and BMI.",
    slots: [
      { key: "time_column", label: "Süre / Zaman Sütunu", type: "continuous", required: true },
      { key: "event_column", label: "Olay Değişkeni (Binary/Kategorik)", type: "binary", required: true },
      { key: "variable", label: "Kovaryat / Prediktör", type: "continuous", required: true },
    ],
  },
  {
    id: "roc",
    name: "ROC Curve Analysis",
    category: "advanced",
    resultId: "roc",
    type: "Betimsel",
    nRequirement: "N ≥ 50",
    dataType: "Sürekli skor + ikili",
    assumption: "İkili altın standart",
    descriptionTR:
      "Bir tanı yönteminin veya anket skorunun hastalığı ya da başarısızlığı ayırt etme gücünü AUC ile ölçer.",
    descriptionEN:
      "Validates diagnostic accuracy and screening capability of a baseline PROM score or clinical metric.",
    scenarioTR:
      "Örnek Vaka: Pre-op KOOS skorunun 12. ay başarısızlık riskini öngörmedeki AUC değerinin bulunması.",
    scenarioEN:
      "Example Case: Computing AUC for how accurately baseline KOOS forecasts poor 1-year outcome.",
    slots: [
      { key: "variable", label: "Sürekli Skor / Tanı Değeri", type: "continuous", required: true },
      {
        key: "group_by",
        label: "İkili Sonuç (Kategorik — önerilen: 2 seviye)",
        type: "categorical",
        cardinality: 2,
        required: true,
      },
    ],
  },
  {
    id: "diagnostic_accuracy",
    name: "Tanısal Doğruluk ve Youden İndeksi (ROC)",
    category: "advanced",
    resultId: "diagnostic_accuracy",
    type: "Tanısal",
    nRequirement: "N ≥ 30",
    dataType: "Sürekli biyobelirteç + İkili tanı",
    assumption: "İkili altın standart tanı",
    descriptionTR:
      "Biyobelirteç veya klinik skor için optimal cut-off (Youden J), Duyarlılık, Özgüllük, PPV, NPV ve LR+ metriklerini hesaplar.",
    descriptionEN:
      "Computes optimal cut-off (Youden's J), Sensitivity, Specificity, PPV, NPV, and LR+ metrics for clinical biomarkers.",
    scenarioTR:
      "Örnek Vaka: Tanısal sınıflandırmada klinik biyobelirteç düzeyi için Youden indeksi ile optimal eşik değer ve tanısal doğruluk tablosu.",
    scenarioEN:
      "Example Case: Optimal cut-off determination via Youden index and diagnostic performance table for clinical biomarker levels.",
    slots: [
      { key: "variable", label: "Sürekli Biyobelirteç / Tanı Skoru", type: "continuous", required: true },
      {
        key: "group_by",
        label: "Altın Standart Tanı (İkili — 2 seviye)",
        type: "categorical",
        cardinality: 2,
        required: true,
      },
    ],
  },
  {
    id: "aft",
    name: "Accelerated Failure Time (AFT)",
    category: "advanced",
    resultId: "regression",
    type: "Tahmini",
    nRequirement: "Olay ≥ 25",
    dataType: "Süre + olay",
    assumption: "Dağılım varsayımı",
    descriptionTR:
      "Olay süresini (revizyon zamanı) doğrudan regresyon katsayıları ile modelleyen parametrik sağkalım yaklaşımıdır.",
    descriptionEN:
      "Parametric survival framework modeling time-to-event directly via regression coefficients.",
    scenarioTR:
      "Örnek Vaka: Revizyon süresinin yaş ve komorbidite ile Weibull AFT modelinde tahmin edilmesi.",
    scenarioEN:
      "Example Case: Weibull AFT prediction of time to revision using age and comorbidity.",
    slots: [
      { key: "time_column", label: "Süre / Zaman Sütunu", type: "continuous", required: true },
      { key: "event_column", label: "Olay Değişkeni (Binary/Kategorik)", type: "binary", required: true },
      { key: "variable", label: "Kovaryat / Prediktör", type: "continuous", required: true },
    ],
  },


];

/**
 * Specialized clinical cohort screening tests (e.g. Table 1 / batch univariate predictors).
 */
export const CLINICAL_SCREENING_CATALOG = [
  {
    id: "univariate_screening",
    name: "Tek Değişkenli Tarama & Karşılaştırma Tablosu (Table 1 / Batch)",
    nameTR: "Tek Değişkenli Tarama & Karşılaştırma Tablosu (Table 1 / Batch)",
    nameEN: "Univariate Screening & Comparison Table (Table 1 / Batch)",
    nameFR: "Dépistage univarié et tableau comparatif (Tableau 1 / Batch)",
    category: "categorical",
    resultId: "univariate_screening",
    type: "Klinik Makale Tablosu",
    nRequirement: "Kohort Verisi",
    dataType: "Kategorik & Sürekli",
    assumption: "Fisher / Ki-Kare / Mann-Whitney",
    descriptionTR:
      "Birincil hedef değişkene (örn. yürüme durumu, komplikasyon, klinik evre) göre birden fazla klinik değişkeni toplu test eder ve yayına hazır karşılaştırma tablosu (Table 1 / Univariate Screening) üretir.",
    descriptionEN:
      "Batch screens multiple candidate predictors against a primary target and generates a publication-ready comparative table with crude OR, 95% CI, and p-values.",
    descriptionFR:
      "Teste par lots plusieurs prédicteurs candidats par rapport à une cible principale et génère un tableau comparatif prêt pour la publication avec OR brut, IC à 95 % et valeurs p.",
    scenarioTR:
      "Örnek Vaka: Şu an yürüme durumu (Evet/Hayır) ile asetabular protrüzyon, skolyoz, psödoartroz, pektoral deformite ve genotip ilişkilerini toplu test edip makale tablosuna dönüştürme.",
    scenarioEN:
      "Example Case: Screening ambulation status against acetabular protrusion, scoliosis, pseudoarthrosis, and genotype.",
    scenarioFR:
      "Exemple : analyse groupée du statut de déambulation en fonction des anomalies squelettiques et du génotype.",
    slots: [
      {
        key: "variable",
        label: "Hedef / Sonuç Değişkeni (Örn: Yürüme Durumu)",
        labelTR: "Hedef / Sonuç Değişkeni (Örn: Yürüme Durumu)",
        labelEN: "Primary Target / Outcome Variable (e.g. Walking Status)",
        labelFR: "Variable cible / résultat principale",
        type: "categorical",
        cardinality: 2,
        required: true,
      },
      {
        key: "comparison_variables",
        descriptionKey: "clinicalSelection.candidatesHelp",
        label: "Karşılaştırma / Aday Değişkenler (Çoklu Seçim)",
        labelTR: "Karşılaştırma / Aday Değişkenler (Çoklu Seçim)",
        labelEN: "Comparison / Candidate Predictors (Multi-select)",
        labelFR: "Variables de comparaison (Sélection multiple)",
        type: "any",
        multiSelect: true,
        required: true,
      },
    ],
  },

  {
    id: "clinical_phenotyping",
    name: "Klinik Fenotipleme (Unsupervised Clustering)",
    nameTR: "Klinik Fenotipleme (Unsupervised Clustering)",
    nameEN: "Clinical Phenotyping (Unsupervised Clustering)",
    nameFR: "Phénotypage clinique (Clustering non supervisé)",
    category: "advanced",
    resultId: "clinical_phenotyping",
    type: "Keşifsel Analiz",
    nRequirement: "N ≥ 10",
    dataType: "Karışık (Sürekli, İkili, Kategorik)",
    assumption: "Gower Distance",
    descriptionTR: "Küçük ve karışık tipte klinik veri setlerinde hastaları yalnızca klinik fenotip özelliklerine göre kümelere ayırır (Gower, HCA — average linkage).",
    descriptionEN: "Clusters patients purely based on clinical phenotype using average-linkage HCA and Gower distance for mixed data types, and profiles the resulting clusters without target leakage.",
    descriptionFR: "Regroupe les patients uniquement sur la base de leur phénotype clinique par CAH à liaison moyenne avec la distance de Gower pour les types de données mixtes et profile les grappes sans fuite de cible.",
    scenarioTR: "Örnek Vaka: Genotip veya hastalık şiddetini modele vermeden, sadece iskelet deformitelerine bakarak hastaların doğal fenotip gruplarını keşfetme.",
    scenarioEN: "Example Case: Discovering natural phenotype groups purely based on skeletal deformities, without leaking genotype or severity.",
    scenarioFR: "Exemple : découverte des groupes phénotypiques naturels uniquement sur la base des déformations squelettiques, sans fuite de génotype ou de gravité.",
    slots: [
      {
        key: "features",
        minCount: 2,
        descriptionKey: "clinicalSelection.featuresHelp",
        label: "Klinik Fenotip Değişkenleri (Clustering Input)",
        labelTR: "Klinik Fenotip Değişkenleri (Clustering Input)",
        labelEN: "Clinical Phenotype Variables (Input)",
        labelFR: "Variables phénotypiques cliniques (Entrées)",
        type: "any",
        multiSelect: true,
        required: true,
      },
      {
        key: "validation_vars",
        descriptionKey: "clinicalSelection.validationHelp",
        label: "Post-hoc Doğrulama (Genotip/Outcome vb.)",
        labelTR: "Post-hoc Doğrulama (Genotip/Outcome vb.)",
        labelEN: "Post-hoc Validation (Genotype/Outcome)",
        labelFR: "Comparaison post-hoc (Génotype / Résultat)",
        type: "any",
        multiSelect: true,
        required: false,
      },
    ],
  },
];

/** Combined catalog containing the 32 canonical statistical tests and clinical screening modules. */
export const ALL_TESTS_CATALOG = [...TEST_CATALOG, ...CLINICAL_SCREENING_CATALOG];

/** Shown in the wizard, but not selectable until the cohort modules are ready. */
export const CLINICAL_MODULE_COMING_SOON_IDS = new Set([
  "univariate_screening",
  "clinical_phenotyping",
]);

export function findCatalogTest(id) {
  return ALL_TESTS_CATALOG.find((t) => t.id === id) || null;
}
